if (!customElements.get("estimated-delivery")) {
  class EstimatedDelivery extends HTMLElement {
    connectedCallback() {
      const dataElement = this.querySelector("[data-estimated-delivery-variants]");

      try {
        this.estimates = JSON.parse(dataElement ? dataElement.textContent : "{}");
      } catch (error) {
        this.estimates = {};
        console.error("Unable to read estimated delivery data.", error);
      }

      this.productRoot = this.closest(".m-main-product") || document;
      this.valueElement = this.querySelector("[data-estimated-delivery-value]");
      this.messageElement = this.querySelector("[data-estimated-delivery-message]");
      this.detailElement = this.querySelector("[data-estimated-delivery-detail]");
      this.handleFormChange = this.handleFormChange.bind(this);
      this.productRoot.addEventListener("input", this.handleFormChange);
      this.productRoot.addEventListener("change", this.handleFormChange);

      this.positionGemstoneStandard = this.positionGemstoneStandard.bind(this);
      this.positionGemstoneStandard();
      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", this.positionGemstoneStandard, { once: true });
      }
      this.update(this.getSelectedVariantId());
    }

    disconnectedCallback() {
      if (!this.productRoot) return;
      this.productRoot.removeEventListener("input", this.handleFormChange);
      this.productRoot.removeEventListener("change", this.handleFormChange);
      document.removeEventListener("DOMContentLoaded", this.positionGemstoneStandard);
    }

    handleFormChange(event) {
      if (!event.target.matches('[name="id"], variant-picker input, variant-picker select')) return;

      // The theme updates the hidden variant ID immediately after a picker change.
      requestAnimationFrame(() => this.update(this.getSelectedVariantId(event.target)));
    }

    positionGemstoneStandard() {
      const gemstoneCard = this.productRoot.querySelector(".petralo-gem-card");
      const gemstoneBlock = gemstoneCard && gemstoneCard.closest(".main-product__block");
      const materialsSections = Array.from(document.querySelectorAll('[data-section-type="collage-with-tabs"]'));
      const materialsSection = materialsSections.find((section) =>
        Array.from(section.querySelectorAll('[role="tab"]')).some(
          (tab) => tab.textContent.trim().toLowerCase() === "gemstone"
        )
      );

      if (!gemstoneCard || !gemstoneBlock || !materialsSection) return false;

      const gemstoneTab = Array.from(materialsSection.querySelectorAll('[role="tab"]')).find(
        (tab) => tab.textContent.trim().toLowerCase() === "gemstone"
      );
      const gemstonePanel = gemstoneTab
        ? materialsSection.querySelector(`.m-tab-content[data-index="${gemstoneTab.dataset.index}"]`)
        : null;
      const content = gemstonePanel && gemstonePanel.querySelector(".m-collage-tab--content-inner");
      const collectionButton = content && content.querySelector(".m-collage-tab--button");

      if (!content) return false;

      let actions = content.querySelector(".petralo-material-actions");
      if (!actions) {
        actions = document.createElement("div");
        actions.className = "petralo-material-actions";

        if (collectionButton) {
          collectionButton.before(actions);
        } else {
          content.append(actions);
        }
      }

      // Keep the collection CTA first, followed by the gemstone-standard drawer trigger.
      if (collectionButton && collectionButton.parentElement !== actions) actions.append(collectionButton);
      actions.append(gemstoneCard);

      gemstoneCard.classList.add("petralo-gem-card--materials");
      gemstoneCard.setAttribute("role", "button");
      gemstoneCard.setAttribute("tabindex", "0");
      gemstoneBlock.classList.add("petralo-gemstone-standard-source");

      const gemstoneLabel = gemstoneCard.querySelector(".petralo-gem-card__pill");
      const gemstoneArrow = gemstoneCard.querySelector(".petralo-gem-card__arrow");
      const gemstoneDrawer = document.getElementById(gemstoneCard.getAttribute("data-gem-open"));
      if (gemstoneLabel) gemstoneLabel.textContent = "Our Gemstone Standard";
      if (gemstoneArrow) gemstoneArrow.remove();
      if (gemstoneDrawer) gemstoneDrawer.classList.add("petralo-gemstone-standard-drawer");

      if (!gemstoneCard.dataset.keyboardEnabled) {
        gemstoneCard.addEventListener("keydown", (event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          gemstoneCard.click();
        });
        gemstoneCard.dataset.keyboardEnabled = "true";
      }

      return true;
    }

    getSelectedVariantId(changedElement) {
      if (changedElement && changedElement.name === "id" && changedElement.value) {
        return changedElement.value;
      }

      const mainForm = this.productRoot.querySelector('form[data-type="add-to-cart-form"] input[name="id"]');
      return mainForm ? mainForm.value : "";
    }

    update(variantId) {
      const leadTime = this.estimates[variantId] || {};
      const message = this.getMessage(leadTime.profile);
      const estimate = message ? this.formatRange(message.minDays, message.maxDays) : "";

      if (this.valueElement) this.valueElement.textContent = estimate;
      if (this.messageElement) this.messageElement.textContent = message ? message.heading : "";
      if (this.detailElement) {
        this.detailElement.textContent = message ? message.detail : "";
        this.detailElement.classList.toggle("is-hidden", !message || !message.detail);
      }
      this.classList.toggle("is-hidden", !estimate);

      this.productRoot.querySelectorAll("[data-estimated-delivery-property]").forEach((input) => {
        input.value = estimate || "";
        input.disabled = !estimate;
      });

      const isMadeToOrder = String(leadTime.profile || "").trim().toLowerCase() === "made to order";
      this.productRoot.querySelectorAll("[data-delivery-status-property]").forEach((input) => {
        input.value = isMadeToOrder ? "Made to Order" : "";
        input.disabled = !isMadeToOrder;
      });
    }

    getMessage(profile) {
      const messages = {
        "made to order": {
          heading: "Made to order",
          detail: "Handcrafted for you. Please allow up to 4 weeks for creation.",
          minDays: 27,
          maxDays: 30,
        },
        "Shipping later": {
          heading: "Ships in 3–4 weeks",
          detail: "",
          minDays: 28,
          maxDays: 31,
        },
        "In stock": {
          heading: "Ready to ship soon",
          detail: "",
          minDays: 7,
          maxDays: 10,
        },
      };

      return messages[profile] || null;
    }

    addCalendarDays(date, numberOfDays) {
      const result = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12);
      result.setDate(result.getDate() + numberOfDays);
      return result;
    }

    formatRange(minDays, maxDays) {
      const today = new Date();
      const from = this.addCalendarDays(today, Math.min(minDays, maxDays));
      const to = this.addCalendarDays(today, Math.max(minDays, maxDays));
      const monthDay = new Intl.DateTimeFormat("en", { month: "short", day: "2-digit" });

      if (from.getTime() === to.getTime()) return monthDay.format(from);
      return `${monthDay.format(from)} — ${monthDay.format(to)}`;
    }
  }

  customElements.define("estimated-delivery", EstimatedDelivery);
}
